import { useCallback, useEffect, useMemo, useReducer, useSyncExternalStore } from 'react'

import type { AppView } from './app-view.js'

import { getRuntimeStatus, subscribeRuntimeStatus } from '../bootstrap/runtime-state.js'
import { authReducer, initialAuthState, selectView } from './app-view.js'
import { hasSession } from './session.js'
import { createIndexedDbTokenStore } from './token-store.js'

export interface UseAppView {
  /** Promote the app to the authenticated surface; passed to the login form's `onAuthenticated`. */
  onAuthenticated: () => void
  /** The single surface to render right now. */
  view: AppView
}

/**
 * Bridge the app's async auth facts onto the pure {@link authReducer}/{@link selectView}:
 *  - discovery status comes straight from the external runtime-state store (it may settle before
 *    or after mount), fed into the reducer only once it leaves `pending`;
 *  - the local session is read once from IndexedDB on mount.
 *
 * The in-flight login lifecycle is owned by the shared login form (its own submit spinner and its
 * injected transport); this hook only exposes the coarse view plus the success signal that flips it
 * to `authenticated`. All view decisions live in the tested reducer/selector.
 */
export const useAppView = (): UseAppView => {
  const store = useMemo(() => createIndexedDbTokenStore(), [])
  const runtimeStatus = useSyncExternalStore(subscribeRuntimeStatus, getRuntimeStatus)
  const [state, dispatch] = useReducer(authReducer, initialAuthState)

  useEffect(() => {
    let alive = true
    void hasSession(store).then((authenticated) => {
      if (alive) dispatch({ authenticated, type: 'session-resolved' })
    })
    return (): void => {
      alive = false
    }
  }, [store])

  useEffect(() => {
    // Feed the reducer only a settled status; `pending` must not be mistaken for `unavailable`.
    if (runtimeStatus === 'ready') dispatch({ available: true, type: 'runtime-resolved' })
    else if (runtimeStatus === 'unavailable') dispatch({ available: false, type: 'runtime-resolved' })
  }, [runtimeStatus])

  const onAuthenticated = useCallback((): void => dispatch({ type: 'login-succeeded' }), [])

  return { onAuthenticated, view: selectView(state) }
}
